import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { axiosInstance } from '@/App';
import { 
  ClipboardCheck, 
  Truck, 
  Building, 
  Wrench,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ArrowLeft,
  Search,
  Calendar,
  MapPin,
  User,
  Eye,
  Download,
  Loader2,
  TrendingUp,
  Clock,
  FileText
} from 'lucide-react';

import OperatorEvaluation from '@/components/OperatorEvaluation';

const iconMap = {
  crane: Building,
  truck: Truck,
  forklift: Truck,
  building: Building,
  ladder: Wrench
};

import { useTranslation } from 'react-i18next';

const SupervisorInspections = ({ user }) => {
  const { t } = useTranslation();
  const [inspections, setInspections] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [showViewDialog, setShowViewDialog] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    template_id: 'all',
    status: 'all',
    search: ''
  });
  const [downloading, setDownloading] = useState(false);

  const handleDownloadPdf = async (inspectionId) => {
    try {
      setDownloading(true);
      const response = await axiosInstance.get(`/inspections/export-pdf/${inspectionId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'];
      const filename = disposition?.match(/filename="(.+)"/)?.[1] || 'inspection.pdf';
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadInspections();
  }, [filters.template_id, filters.status]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [templatesRes, statsRes] = await Promise.all([
        axiosInstance.get('/inspections/all-templates'),
        axiosInstance.get('/inspections/stats/summary')
      ]);
      // Use the 'all' array which combines built-in and custom
      setTemplates(templatesRes.data.all || []);
      setStats(statsRes.data);
      await loadInspections();
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load inspections');
    } finally {
      setLoading(false);
    }
  };

  const loadInspections = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.template_id && filters.template_id !== 'all') params.append('template_id', filters.template_id);
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      params.append('limit', '100');
      
      const res = await axiosInstance.get(`/inspections?${params.toString()}`);
      setInspections(res.data);
    } catch (error) {
      console.error('Error loading inspections:', error);
    }
  };

  const viewInspection = (inspection) => {
    setSelectedInspection(inspection);
    setShowViewDialog(true);
  };

  const filteredInspections = inspections.filter(i => {
    if (!filters.search) return true;
    const search = filters.search.toLowerCase();
    return (
      i.template_name?.toLowerCase().includes(search) ||
      i.location?.toLowerCase().includes(search) ||
      i.inspector_name?.toLowerCase().includes(search) ||
      i.equipment_name?.toLowerCase().includes(search) ||
      i.equipment_id?.toLowerCase().includes(search)
    );
  });

  // Group checklist by category for view dialog
  const groupedChecklist = selectedInspection?.checklist_results?.reduce((acc, result) => {
    // Find the category from template
    const template = templates.find(t => t.id === selectedInspection.template_id);
    const category = result.category || 'General';
    if (!acc[category]) acc[category] = [];
    acc[category].push(result);
    return acc;
  }, {}) || {};

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <Button
          variant="ghost"
          onClick={() => window.history.back()}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('inspections.title')}</h1>
            <p className="text-gray-600">{t('inspections.monitorReview', 'Monitor and review equipment inspection records')}</p>
          </div>
        </div>

        <Tabs defaultValue="inspections" className="space-y-6">
          <TabsList>
            <TabsTrigger value="inspections">
              <ClipboardCheck className="w-4 h-4 mr-2" />
              {t('inspections.inspectionsTab', 'Inspections')}
            </TabsTrigger>
            <TabsTrigger value="evaluations" data-testid="supervisor-evaluations-tab">
              <FileText className="w-4 h-4 mr-2" />
              {t('inspections.evaluationsTab', 'Evaluations')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="inspections">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <ClipboardCheck className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.total}</p>
                    <p className="text-sm text-gray-500">Total</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.passed}</p>
                    <p className="text-sm text-gray-500">Passed</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <XCircle className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.failed}</p>
                    <p className="text-sm text-gray-500">Failed</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <TrendingUp className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.pass_rate}%</p>
                    <p className="text-sm text-gray-500">{t('inspections.passRate')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search by location, inspector, equipment..."
                    value={filters.search}
                    onChange={(e) => setFilters({...filters, search: e.target.value})}
                    className="pl-10"
                  />
                </div>
              </div>
              <Select value={filters.template_id} onValueChange={(v) => setFilters({...filters, template_id: v})}>
                <SelectTrigger className="w-full sm:w-[200px]">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {templates.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filters.status} onValueChange={(v) => setFilters({...filters, status: v})}>
                <SelectTrigger className="w-full sm:w-[150px]">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pass">Passed</SelectItem>
                  <SelectItem value="fail">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Inspections List */}
        <Card>
          <CardHeader>
            <CardTitle>{t('inspections.recentInspections')}</CardTitle>
            <CardDescription>
              {filteredInspections.length} inspection{filteredInspections.length !== 1 ? 's' : ''} found
            </CardDescription>
          </CardHeader>
          <CardContent>
            {filteredInspections.length === 0 ? (
              <div className="text-center py-12">
                <ClipboardCheck className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">{t('inspections.noInspections')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredInspections.map(inspection => {
                  const IconComponent = iconMap[templates.find(t => t.id === inspection.template_id)?.icon] || ClipboardCheck;
                  return (
                    <div
                      key={inspection.id}
                      className="p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => viewInspection(inspection)}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${inspection.overall_status === 'pass' ? 'bg-green-100' : 'bg-red-100'}`}>
                            <IconComponent className={`h-5 w-5 ${inspection.overall_status === 'pass' ? 'text-green-600' : 'text-red-600'}`} />
                          </div>
                          <div>
                            <p className="font-medium">{inspection.template_name}</p>
                            {inspection.equipment_name && (
                              <p className="text-sm text-gray-600">{inspection.equipment_name} {inspection.equipment_id && `(${inspection.equipment_id})`}</p>
                            )}
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 mt-1">
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {inspection.location}
                              </span>
                              <span className="flex items-center gap-1">
                                <User className="h-3 w-3" />
                                {inspection.inspector_name}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {new Date(inspection.created_at).toLocaleString()}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={inspection.overall_status === 'pass' ? 'default' : 'destructive'}>
                            {inspection.overall_status === 'pass' ? 'Passed' : 'Failed'}
                          </Badge>
                          <Button size="sm" variant="ghost">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="evaluations">
            <OperatorEvaluation user={user} />
          </TabsContent>
        </Tabs>
      </main>
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle className="flex items-center gap-2">
              {selectedInspection?.overall_status === 'pass' ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : (
                <XCircle className="h-5 w-5 text-red-600" />
              )}
              {selectedInspection?.template_name}
            </DialogTitle>
            <DialogDescription className="flex items-center justify-between">
              <span>Inspection details and checklist results</span>
              <Button 
                size="sm" 
                variant="outline" 
                data-testid="download-inspection-pdf-btn"
                onClick={() => handleDownloadPdf(selectedInspection?.id)}
                disabled={downloading}
                className="ml-2"
              >
                {downloading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Download className="h-4 w-4 mr-1" />}
                {t('inspections.downloadPdf')}
              </Button>
            </DialogDescription>
          </DialogHeader>

          {selectedInspection && (
            <div className="flex-1 overflow-y-auto px-6">
              <div className="space-y-6 py-4">
                {/* Summary */}
                <div className={`p-4 rounded-lg ${selectedInspection.overall_status === 'pass' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold">
                        {selectedInspection.overall_status === 'pass' ? 'Inspection Passed' : 'Inspection Failed'}
                      </p>
                      <p className="text-sm text-gray-600">
                        {new Date(selectedInspection.created_at).toLocaleString()}
                      </p>
                    </div>
                    <Badge variant={selectedInspection.frequency === 'daily' ? 'default' : 'secondary'}>
                      {selectedInspection.frequency}
                    </Badge>
                  </div>
                </div>

                {/* Details */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Inspector</p>
                    <p className="font-medium">{selectedInspection.inspector_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Location</p>
                    <p className="font-medium">{selectedInspection.location}</p>
                  </div>
                  {selectedInspection.equipment_name && (
                    <div>
                      <p className="text-sm text-gray-500">Equipment</p>
                      <p className="font-medium">{selectedInspection.equipment_name}</p>
                    </div>
                  )}
                  {selectedInspection.equipment_id && (
                    <div>
                      <p className="text-sm text-gray-500">Equipment ID</p>
                      <p className="font-medium">{selectedInspection.equipment_id}</p>
                    </div>
                  )}
                  {selectedInspection.mileage_hours && (
                    <div>
                      <p className="text-sm text-gray-500">Mileage/Hours</p>
                      <p className="font-medium">{selectedInspection.mileage_hours}</p>
                    </div>
                  )}
                </div>

                {/* Checklist Results */}
                <div>
                  <h3 className="font-semibold mb-3">Checklist Results</h3>
                  {(() => {
                    const results = selectedInspection.checklist_results || [];
                    const categories = {};
                    results.forEach(r => {
                      const cat = r.category || 'General';
                      if (!categories[cat]) categories[cat] = [];
                      categories[cat].push(r);
                    });
                    return Object.entries(categories).map(([category, items]) => (
                      <div key={category} className="mb-4">
                        <div className="bg-gray-800 text-white text-sm font-medium px-3 py-1.5 rounded-t">
                          {category}
                        </div>
                        <div className="border border-t-0 rounded-b divide-y">
                          {items.map((result, idx) => (
                            <div key={idx} className={`flex items-start gap-3 px-3 py-2 ${result.status === 'fail' ? 'bg-red-50' : ''}`}>
                              {result.status === 'pass' && <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />}
                              {result.status === 'fail' && <XCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />}
                              {result.status === 'na' && <div className="h-4 w-4 rounded-full bg-gray-300 mt-0.5 flex-shrink-0" />}
                              <div className="flex-1 min-w-0">
                                <div className="text-sm flex items-center gap-1 flex-wrap">
                                  <span>{result.item_text || result.item_id}</span>
                                  {result.critical && (
                                    <Badge variant="destructive" className="text-[10px] px-1 py-0">CRITICAL</Badge>
                                  )}
                                </div>
                                {result.notes && (
                                  <p className="text-xs text-red-600 mt-1">Note: {result.notes}</p>
                                )}
                              </div>
                              <Badge 
                                variant={result.status === 'pass' ? 'default' : result.status === 'fail' ? 'destructive' : 'secondary'} 
                                className="text-xs flex-shrink-0"
                              >
                                {result.status.toUpperCase()}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    ));
                  })()}
                </div>

                {/* Notes */}
                {selectedInspection.notes && (
                  <div>
                    <h3 className="font-semibold mb-2">Additional Notes</h3>
                    <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">{selectedInspection.notes}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupervisorInspections;
